import { DragDropContext, Draggable, Droppable, type DropResult } from '@hello-pangea/dnd'
import type { StoreCollectionItem } from '@/lib/api/commerce/store-themes'
import { STORE_COLLECTION_KINDS } from '@mediardex/contracts'
import { MdAdd, MdDragIndicator, MdVisibilityOff } from 'react-icons/md'

interface CollectionListProps {
    collections: StoreCollectionItem[]
    currentCollectionId: number | null
    onSelect: (collectionId: number) => void
    onReorder: (collections: StoreCollectionItem[]) => void
    onCreate: () => void
}

/** Coleções da loja, que se reordenam arrastando (a ordem vale no seletor da vitrine de produtos). */
export default function CollectionList({
    collections,
    currentCollectionId,
    onSelect,
    onReorder,
    onCreate,
}: CollectionListProps) {
    const handleDragEnd = (result: DropResult) => {
        if (!result.destination || result.destination.index === result.source.index) return
        const next = [...collections]
        const [moved] = next.splice(result.source.index, 1)
        next.splice(result.destination.index, 0, moved)
        onReorder(next)
    }

    const itemClass = (selected: boolean) =>
        `flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm ${selected ? 'bg-primary/10 font-medium text-primary' : 'hover:bg-base-200'}`

    return (
        <div className="flex flex-col gap-2">
            <span className="px-1 text-xs font-semibold uppercase tracking-wide text-base-content/60">Coleções</span>

            {collections.length === 0 && (
                <p className="px-1 text-xs text-base-content/60">
                    Agrupe produtos (verão, lançamentos, mais vendidos) numa página própria, com SEO.
                </p>
            )}

            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="store-collections">
                    {(provided) => (
                        <ul ref={provided.innerRef} {...provided.droppableProps} className="flex flex-col gap-0.5">
                            {collections.map((collection, index) => (
                                <Draggable key={collection.id} draggableId={`collection-${collection.id}`} index={index}>
                                    {(drag) => (
                                        <li ref={drag.innerRef} {...drag.draggableProps} className="flex items-center gap-1">
                                            <span {...drag.dragHandleProps} className="cursor-grab text-base-content/40" title="Arrastar">
                                                <MdDragIndicator />
                                            </span>
                                            <button
                                                type="button"
                                                className={itemClass(currentCollectionId === collection.id)}
                                                onClick={() => onSelect(collection.id)}
                                            >
                                                <span className="flex min-w-0 flex-1 flex-col">
                                                    <span className={`truncate ${collection.visible ? '' : 'text-base-content/50'}`}>
                                                        {collection.title}
                                                    </span>
                                                    <span className="truncate text-xs font-normal text-base-content/50">
                                                        {STORE_COLLECTION_KINDS.find((k) => k.value === collection.kind)?.label}
                                                    </span>
                                                </span>
                                                {!collection.visible && (
                                                    <MdVisibilityOff className="text-base-content/40" title="Escondida" />
                                                )}
                                            </button>
                                        </li>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </ul>
                    )}
                </Droppable>
            </DragDropContext>

            <button type="button" className="btn btn-sm btn-ghost justify-start" onClick={onCreate}>
                <MdAdd /> Nova coleção
            </button>
        </div>
    )
}
